// Development Studies Unit Tests - Years 11-13
// Assessment materials

export const year11DevelopmentUnitTests = {
  year: 11,
  subject: "Development Studies",
  strands: {
    strand1: {
      name: "What is Development?",
      tests: [
        {
          id: "dst1-11-test",
          title: "Development Concepts Test",
          duration: "45 minutes",
          totalMarks: 40,
          questions: [
            {
              id: 1,
              question: "Define the term 'development' in your own words.",
              marks: 3,
              answer: "Development is a multi-dimensional process of change that improves the quality of life for individuals and communities. It includes economic, social, political, cultural, environmental, and spiritual aspects."
            },
            {
              id: 2,
              question: "Name the SIX aspects of development.",
              marks: 6,
              answer: "1. Economic development\n2. Social development\n3. Political development\n4. Cultural development\n5. Environmental development\n6. Spiritual development"
            },
            {
              id: 3,
              question: "Explain why development does not always mean economic development. Give an example.",
              marks: 4,
              answer: "Development includes many aspects beyond just economic growth. A country can have high GDP but poor social services, environmental damage, or human rights abuses. Example: Some oil-rich countries have high incomes but low education levels or gender inequality."
            },
            {
              id: 4,
              question: "What does GDP per capita measure? Write the formula.",
              marks: 3,
              answer: "GDP per capita measures the average income per person in a country.\nFormula: GDP per capita = Total GDP ÷ Population"
            },
            {
              id: 5,
              question: "What THREE indicators are combined to calculate the Human Development Index (HDI)?",
              marks: 3,
              answer: "1. Life expectancy (health)\n2. Mean years of schooling (education)\n3. GNI per capita (standard of living)"
            },
            {
              id: 6,
              question: "Give TWO limitations of using GDP per capita as a measure of development.",
              marks: 4,
              answer: "1. It doesn't show how wealth is distributed - a country can have high average income but large inequality between rich and poor.\n2. It doesn't measure important aspects like happiness, freedom, environmental quality, or cultural well-being."
            },
            {
              id: 7,
              question: "Calculate the GDP per capita for a country with:\n- Total GDP: $5 billion\n- Population: 250,000",
              marks: 3,
              answer: "GDP per capita = GDP ÷ Population\n= $5,000,000,000 ÷ 250,000\n= $20,000 per person"
            }
          ]
        }
      ]
    },
    strand2: {
      name: "Economic Development",
      tests: [
        {
          id: "dst2-11-test",
          title: "Economic Systems Test",
          duration: "40 minutes",
          totalMarks: 35,
          questions: [
            {
              id: 1,
              question: "Define the following terms:\n(a) Subsistence economy\n(b) Commercial economy",
              marks: 4,
              answer: "(a) Subsistence economy: An economy where people produce mainly for their own consumption, not for sale\n(b) Commercial economy: An economy based on buying and selling goods and services for profit"
            },
            {
              id: 2,
              question: "Name the FOUR sectors of economic development and give one example of each from Vanuatu.",
              marks: 8,
              answer: "1. Primary sector - Example: Fishing, copra farming, logging\n2. Secondary sector - Example: Coconut oil processing, handicraft making\n3. Tertiary sector - Example: Tourism, banking, transport\n4. Quaternary sector - Example: IT services, research"
            },
            {
              id: 3,
              question: "Why does Vanuatu have a 'mixed economy'?",
              marks: 4,
              answer: "Vanuatu has a mixed economy because it combines both subsistence and commercial economic activities. Rural areas are mainly subsistence-based (farming for family needs), while urban areas have more commercial activities (businesses, wage employment). Both systems coexist."
            },
            {
              id: 4,
              question: "What is the difference between subsistence and commercial agriculture?",
              marks: 4,
              answer: "Subsistence agriculture: Farming to feed the farmer's family, little or no surplus for sale, uses traditional methods, small scale.\nCommercial agriculture: Farming to sell produce for profit, larger scale, uses modern methods, focused on cash crops."
            },
            {
              id: 5,
              question: "Why is the primary sector dominant in Vanuatu's economy? Give TWO reasons.",
              marks: 4,
              answer: "1. Most people live in rural areas and rely on agriculture, fishing, and forestry for their livelihoods\n2. Limited industrialization due to small domestic market, lack of infrastructure, and distance from major markets"
            }
          ]
        }
      ]
    },
    strand3: {
      name: "Promoting Development",
      tests: [
        {
          id: "dst3-11-test",
          title: "International Aid Test",
          duration: "40 minutes",
          totalMarks: 35,
          questions: [
            {
              id: 1,
              question: "Define the following terms:\n(a) International aid\n(b) ODA (Official Development Assistance)\n(c) NGO",
              marks: 6,
              answer: "(a) International aid: Assistance given by governments and organizations to help developing countries\n(b) ODA: Aid from government sources, given on favorable terms (grants or low-interest loans)\n(c) NGO: Non-Governmental Organization - private groups that provide aid and services"
            },
            {
              id: 2,
              question: "What is the difference between bilateral aid and multilateral aid? Give an example of each.",
              marks: 4,
              answer: "Bilateral aid: Aid given directly from one country to another (Example: Australia giving aid to Vanuatu through DFAT)\nMultilateral aid: Aid given through international organizations (Example: World Bank loans, UN programs)"
            },
            {
              id: 3,
              question: "Name THREE major aid donors to Pacific Island countries.",
              marks: 3,
              answer: "1. Australia (DFAT)\n2. New Zealand (MFAT)\n3. Japan (JICA)\n(Also acceptable: European Union, China, USA)"
            },
            {
              id: 4,
              question: "Give THREE advantages and THREE disadvantages of receiving foreign aid.",
              marks: 6,
              answer: "Advantages:\n1. Helps build infrastructure (roads, schools, hospitals)\n2. Provides emergency relief in disasters\n3. Transfers technology and skills\n\nDisadvantages:\n1. Creates dependency on donors\n2. May come with conditions (tied aid)\n3. Can support corruption or not reach those in need"
            },
            {
              id: 5,
              question: "What is meant by 'aid dependency'? Why is it a concern?",
              marks: 4,
              answer: "Aid dependency: When a country relies too heavily on foreign aid and cannot function without it.\nConcerns: It reduces a country's ability to be self-sufficient, may influence government policies to please donors rather than citizens, and creates vulnerability if aid is reduced."
            }
          ]
        }
      ]
    },
    strand4: {
      name: "Development and Environment",
      tests: [
        {
          id: "dst4-11-test",
          title: "Environment Test",
          duration: "45 minutes",
          totalMarks: 40,
          questions: [
            {
              id: 1,
              question: "Explain the greenhouse effect in your own words.",
              marks: 4,
              answer: "The greenhouse effect is when gases in Earth's atmosphere trap heat from the sun. Sunlight passes through the atmosphere and warms the Earth. The Earth re-radiates heat, but greenhouse gases (like CO2 and methane) trap some of this heat, keeping the Earth warm. Too many greenhouse gases cause global warming."
            },
            {
              id: 2,
              question: "Name THREE greenhouse gases and give one source for each.",
              marks: 6,
              answer: "1. Carbon dioxide (CO2) - burning fossil fuels, deforestation\n2. Methane (CH4) - agriculture (cattle), landfills, rice paddies\n3. Nitrous oxide (N2O) - fertilizers, industrial processes"
            },
            {
              id: 3,
              question: "What is the difference between renewable and non-renewable resources? Give two examples of each.",
              marks: 6,
              answer: "Renewable resources: Can be replaced naturally over time\nExamples: Forests, fish stocks, fresh water\n\nNon-renewable resources: Cannot be replaced once used\nExamples: Oil, coal, minerals (gold, copper)"
            },
            {
              id: 4,
              question: "Describe THREE impacts of climate change on Vanuatu.",
              marks: 6,
              answer: "1. Sea level rise - threatens coastal communities and low-lying islands\n2. More intense cyclones - increases damage to infrastructure and lives\n3. Coral bleaching - damages reef ecosystems important for fishing and tourism\n(Also: drought, changes in rainfall, threats to agriculture)"
            },
            {
              id: 5,
              question: "Explain what is meant by 'sustainable use' of resources. Give an example from Vanuatu.",
              marks: 4,
              answer: "Sustainable use: Using resources at a rate that allows them to be replaced or regenerated, so they remain available for future generations.\nExample: The traditional 'tabu' system in Vanuatu - closing fishing areas or reefs for periods to allow fish stocks to recover before harvesting again."
            },
            {
              id: 6,
              question: "Why are traditional conservation practices like 'tabu' important for environmental protection?",
              marks: 4,
              answer: "Tabu practices are important because they:\n1. Allow resources to recover between harvests\n2. Are managed by local communities who understand local conditions\n3. Have been effective for centuries\n4. Respect natural cycles of reproduction and growth"
            }
          ]
        }
      ]
    },
    strand5: {
      name: "Social, Cultural and Political Development",
      tests: [
        {
          id: "dst5-11-test",
          title: "Human Societies Test",
          duration: "40 minutes",
          totalMarks: 35,
          questions: [
            {
              id: 1,
              question: "Name the FOUR stages of human society development in order.",
              marks: 4,
              answer: "1. Hunter-gatherer society\n2. Agricultural society\n3. Industrial society\n4. Post-industrial society"
            },
            {
              id: 2,
              question: "Describe THREE features of traditional Vanuatu society.",
              marks: 6,
              answer: "1. Based on subsistence agriculture - growing food for family needs\n2. Strong extended family and clan systems - communal living and support\n3. Chief-based leadership - traditional authority and decision-making\n(Also: oral traditions, communal land ownership, rich customs)"
            },
            {
              id: 3,
              question: "What is population growth rate? What is Vanuatu's approximate population?",
              marks: 3,
              answer: "Population growth rate: The rate at which a population increases or decreases, usually expressed as a percentage per year.\nVanuatu's population: Approximately 320,000 people"
            },
            {
              id: 4,
              question: "Give THREE negative effects of rapid population growth.",
              marks: 6,
              answer: "1. Pressure on natural resources (land, water, forests)\n2. Strain on services (schools, hospitals, infrastructure)\n3. Unemployment and poverty\n(Also: environmental degradation, overcrowded cities, food insecurity)"
            },
            {
              id: 5,
              question: "Why does Vanuatu have so many different languages?",
              marks: 4,
              answer: "Vanuatu has many languages because:\n1. Islands are isolated from each other by sea\n2. Mountain terrain separates communities on larger islands\n3. Historically limited contact between groups led to language divergence\n4. Different waves of migration brought different languages"
            }
          ]
        }
      ]
    }
  }
};

export const year12DevelopmentUnitTests = {
  year: 12,
  subject: "Development Studies",
  strands: {
    strand1: {
      name: "Development Analysis",
      tests: [
        {
          id: "dst1-12-test",
          title: "Development Inequalities Test",
          duration: "50 minutes",
          totalMarks: 45,
          questions: [
            {
              id: 1,
              question: "What is meant by the 'development gap'?",
              marks: 3,
              answer: "The development gap refers to the difference in living standards, income, and quality of life between developed (rich) countries and developing (poor) countries."
            },
            {
              id: 2,
              question: "Explain the difference between MDC/MEDC and LDC/LEDC. Give two examples of each.",
              marks: 6,
              answer: "MDC/MEDC (More/Economically Developed Country): High income, good services, high HDI\nExamples: USA, Japan, Australia, Germany\n\nLDC/LEDC (Least/Less Economically Developed Country): Low income, limited services, low HDI\nExamples: Vanuatu, Haiti, Mali, Nepal"
            },
            {
              id: 3,
              question: "Explain TWO ways that colonialism contributed to the development gap.",
              marks: 6,
              answer: "1. Resource extraction: Colonial powers took valuable resources (minerals, timber, crops) from colonies for their own benefit, leaving colonies without wealth to develop\n2. Trade patterns: Colonies were made to export raw materials cheaply and import expensive manufactured goods, creating unfair trade relationships that continue today"
            },
            {
              id: 4,
              question: "What is a Quality of Life Survey? What is its purpose?",
              marks: 4,
              answer: "A Quality of Life Survey is a research method to assess living conditions in a local community by collecting data on various development indicators (income, education, health, housing, access to services, etc.). Its purpose is to measure the well-being of a community and identify areas needing improvement."
            },
            {
              id: 5,
              question: "Name FOUR indicators you might include in a Quality of Life Survey.",
              marks: 4,
              answer: "1. Income/employment\n2. Housing quality\n3. Access to clean water\n4. Education level\n(Also: access to healthcare, sanitation, electricity, food security)"
            }
          ]
        }
      ]
    },
    strand2: {
      name: "Food Security and Economy",
      tests: [
        {
          id: "dst2-12-test",
          title: "Food Security Test",
          duration: "45 minutes",
          totalMarks: 40,
          questions: [
            {
              id: 1,
              question: "What are the FOUR dimensions of food security?",
              marks: 4,
              answer: "1. Availability - enough food produced or imported\n2. Access - people can afford and reach food\n3. Utilization - proper nutrition and health\n4. Stability - consistent access over time"
            },
            {
              id: 2,
              question: "Define the following terms:\n(a) Poverty\n(b) Malnutrition\n(c) Food security",
              marks: 6,
              answer: "(a) Poverty: The state of being extremely poor and lacking basic necessities\n(b) Malnutrition: Lack of proper nutrition from insufficient or imbalanced food\n(c) Food security: When all people have access to sufficient, safe, nutritious food at all times"
            },
            {
              id: 3,
              question: "Give THREE causes of food insecurity in Pacific Island countries.",
              marks: 6,
              answer: "1. Import dependency - relying on imported foods that can be expensive or disrupted\n2. Climate change - affecting agriculture through drought, floods, storms\n3. Declining traditional agriculture - as people move to towns or grow cash crops instead\n(Also: isolation of communities, poor storage, unhealthy diet changes)"
            },
            {
              id: 4,
              question: "How does climate change affect agriculture in Vanuatu? Give TWO examples.",
              marks: 4,
              answer: "1. Drought: Reduced rainfall affects crop growth and water availability\n2. Cyclones: Destroy crops and agricultural infrastructure\n(Also: sea level rise saltwater intrusion into farmland, coral bleaching affecting fishing)"
            },
            {
              id: 5,
              question: "Suggest TWO solutions to improve food security in Vanuatu.",
              marks: 4,
              answer: "1. Promote local food production - support traditional agriculture, provide training on sustainable farming techniques\n2. Improve food storage and distribution - reduce food waste, better infrastructure to get food to remote communities\n(Also: climate-resilient crops, school gardens, food preservation)"
            }
          ]
        }
      ]
    }
  }
};

export const year13DevelopmentUnitTests = {
  year: 13,
  subject: "Development Studies",
  strands: {
    strand1: {
      name: "Development Theories",
      tests: [
        {
          id: "dst1-13-test",
          title: "Development Theories Test",
          duration: "50 minutes",
          totalMarks: 50,
          questions: [
            {
              id: 1,
              question: "Name Rostow's FIVE stages of economic growth.",
              marks: 5,
              answer: "1. Traditional society\n2. Preconditions for take-off\n3. Take-off\n4. Drive to maturity\n5. Age of high mass consumption"
            },
            {
              id: 2,
              question: "Explain TWO criticisms of modernization theory.",
              marks: 6,
              answer: "1. Western-centric: It assumes all countries should follow the Western development path, ignoring that different cultures and contexts may require different approaches\n2. Ignores historical exploitation: It doesn't account for how colonialism and unfair trade have disadvantaged developing countries"
            },
            {
              id: 3,
              question: "Explain the core-periphery model in dependency theory.",
              marks: 6,
              answer: "The core-periphery model shows that the world economy is divided into:\n- Core countries (wealthy, powerful): USA, Europe, Japan - they control trade, technology, and finance\n- Periphery countries (poor, dependent): Most of Africa, Pacific Islands - they export raw materials at low prices and remain dependent on the core\n- Semi-periphery: NICs like China, Brazil - industrializing but still not equal to core"
            },
            {
              id: 4,
              question: "What is sustainable development? Name the THREE pillars.",
              marks: 5,
              answer: "Sustainable development: Development that meets present needs without compromising the ability of future generations to meet their own needs.\n\nThree pillars:\n1. Economic sustainability\n2. Social sustainability\n3. Environmental sustainability"
            },
            {
              id: 5,
              question: "Compare modernization theory and dependency theory. Which do you think better explains development in the Pacific? Explain your answer.",
              marks: 10,
              answer: "Modernization theory says poor countries need to follow the Western path of industrialization and will eventually 'catch up.' It emphasizes internal factors.\n\nDependency theory says poor countries remain poor because of exploitation by rich countries through unfair trade and historical colonialism. It emphasizes external factors.\n\nFor the Pacific: Dependency theory may better explain Pacific development because:\n- Colonial history shaped current economic structures\n- Small islands are vulnerable to global economic forces\n- Trade terms favor wealthy countries\n- Limited resources make industrialization difficult\n\nHowever, modernization theory's emphasis on education and infrastructure is also relevant."
            }
          ]
        }
      ]
    },
    strand2: {
      name: "Globalization",
      tests: [
        {
          id: "dst2-13-test",
          title: "Globalization Test",
          duration: "45 minutes",
          totalMarks: 40,
          questions: [
            {
              id: 1,
              question: "Define globalization.",
              marks: 3,
              answer: "Globalization is the increasing interconnection of the world's economies, cultures, and populations through trade, technology, communication, and movement of people."
            },
            {
              id: 2,
              question: "Name THREE drivers (causes) of globalization.",
              marks: 3,
              answer: "1. Technology (internet, transport, communications)\n2. Trade liberalization (removal of trade barriers)\n3. Multinational corporations\n(Also: international organizations like WTO, cheaper travel)"
            },
            {
              id: 3,
              question: "Give THREE positive and THREE negative effects of globalization.",
              marks: 6,
              answer: "Positive:\n1. Economic growth opportunities and access to new markets\n2. Technology transfer to developing countries\n3. Cultural exchange and global awareness\n\nNegative:\n1. Widening inequality between and within countries\n2. Loss of local cultures and traditions\n3. Environmental damage from increased production and transport"
            },
            {
              id: 4,
              question: "How has globalization affected Vanuatu? Give TWO positive and TWO negative examples.",
              marks: 8,
              answer: "Positive:\n1. Tourism growth - bringing income and employment\n2. RSE scheme - allowing workers to earn money in New Zealand\n\nNegative:\n1. Import dependency - relying on imported foods and goods\n2. Cultural erosion - Western influences affecting traditional values\n(Also: brain drain, vulnerability to global economic crises)"
            },
            {
              id: 5,
              question: "What is a multinational corporation (MNC)? Give one advantage and one disadvantage of MNCs for developing countries.",
              marks: 4,
              answer: "MNC: A company that operates in multiple countries.\n\nAdvantage: Creates jobs and brings investment, technology, and skills to developing countries\n\nDisadvantage: Profits often leave the country, may exploit workers or resources, can dominate local businesses"
            }
          ]
        }
      ]
    },
    strand5: {
      name: "Governance",
      tests: [
        {
          id: "dst5-13-test",
          title: "Governance Test",
          duration: "45 minutes",
          totalMarks: 40,
          questions: [
            {
              id: 1,
              question: "What are the key principles of good governance? Name FOUR.",
              marks: 4,
              answer: "1. Participation - citizens involved in decisions\n2. Transparency - open government\n3. Accountability - leaders answer to people\n4. Rule of law - fair laws applied equally\n(Also: effectiveness, equity, responsiveness)"
            },
            {
              id: 2,
              question: "Describe Vanuatu's system of government.",
              marks: 4,
              answer: "Vanuatu is a constitutional democracy with:\n- President as ceremonial head of state\n- Prime Minister as head of government\n- Parliament with 52 elected members\n- Provincial governments for decentralized administration"
            },
            {
              id: 3,
              question: "Give THREE governance challenges facing Vanuatu.",
              marks: 6,
              answer: "1. Political instability - frequent votes of no confidence and changes of government\n2. Corruption - misuse of public funds and resources\n3. Limited capacity - shortage of skilled government workers\n(Also: urban-rural disparities in services, transparency issues)"
            },
            {
              id: 4,
              question: "What is the Universal Declaration of Human Rights? Name FOUR rights it protects.",
              marks: 6,
              answer: "The Universal Declaration of Human Rights (1948) is an international document adopted by the United Nations setting out fundamental human rights for all people.\n\nRights include:\n1. Right to life, liberty, and security\n2. Freedom from torture and slavery\n3. Right to fair trial\n4. Freedom of expression and religion\n(Also: right to education, work, healthcare)"
            },
            {
              id: 5,
              question: "Why is transparency important in governance? Give an example of how lack of transparency can harm development.",
              marks: 6,
              answer: "Transparency is important because:\n- It allows citizens to know how decisions are made and money is spent\n- It helps prevent corruption and misuse of resources\n- It builds trust between government and people\n\nExample: If government budgets are not transparent, officials may divert funds for personal use instead of building schools or hospitals, harming development."
            }
          ]
        }
      ]
    }
  }
};

export default {
  year11DevelopmentUnitTests,
  year12DevelopmentUnitTests,
  year13DevelopmentUnitTests
};
